import moment from 'moment'
import { Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'

// --- helpers ---
const fmtDate = d => (d ? moment(d).format('YYYY-MM-DD') : '-')
const INFAKT_INVOICE_URL = 'https://front.infakt.pl/faktura/korekta'

export default function KorektaHeader({
	invoice,
	customerName,
	placowkaId,
	onAssignClick,
	onEdit,
	onDelete,
	onPushToInfakt,
	deleting = false,
	pushing = false,
}) {
	const { t } = useTranslation()
	const number = invoice?.number ?? '—'
	const date = fmtDate(invoice?.invoiceDate)
	const name = customerName || t('korekty.list.table.assignPlacowka')
	const hasAssignedPlacowka = Boolean(placowkaId)
	const infaktInvoiceUuid = invoice?.infaktInvoiceUuid || invoice?.infakt_invoice_uuid
	const hasInfaktInvoice = Boolean(infaktInvoiceUuid)

	const handleDownload = () => {
		if (!infaktInvoiceUuid) return
		window.open(
			`${INFAKT_INVOICE_URL}/${encodeURIComponent(infaktInvoiceUuid)}?active_tab=in_progress`,
			'_blank',
			'noopener,noreferrer'
		)
	}

	return (
		<header className="mb-6 flex flex-col md:flex-row md:items-center md:justify-between gap-4">
			{/* Left side: details */}
			<div>
				<h1 className="flex items-center gap-2 text-2xl font-bold text-gray-900">
					<span>{t('korekty.details.header.title', { number })}</span>
					{hasInfaktInvoice && (
						<img
							src="/infakt-logo.svg"
							alt="inFakt"
							title="inFakt"
							className="h-6 w-6 shrink-0"
						/>
					)}
				</h1>
				<p className="text-sm text-gray-500">
					{t('korekty.details.header.invoiceDate')}: <span className="font-medium text-gray-900">{date}</span>
				</p>
				<p className="text-sm text-gray-500">
					{t('korekty.list.table.company')}:{' '}
					<span className="font-medium text-gray-900">
						{invoice?.companyName || t('korekty.list.table.na')}
					</span>
				</p>
				<p className="text-sm text-gray-500">
					{t('korekty.details.header.correctedInvoiceNumber')}:{' '}
					<span className="font-medium text-gray-900">
						{invoice?.correctedInvoiceNumber || t('korekty.list.table.na')}
					</span>
				</p>
				<p className="text-sm text-gray-500">
					{t('korekty.list.table.infaktClientCompanyName')}:{' '}
					<span className="font-medium text-gray-900">
						{invoice?.infakt_client_company_name || t('korekty.list.table.na')}
					</span>
				</p>
				<p className="text-sm text-gray-500">
					{t('korekty.details.placowka.title')}:{' '}
					{hasAssignedPlacowka ? (
						<Link
							to={`/klienci/placowki/details/${placowkaId}`}
							className="font-medium text-blue-600 hover:text-blue-800 hover:underline">
							{name}
						</Link>
					) : (
						<button
							type="button"
							onClick={onAssignClick}
							className="font-medium text-blue-600 hover:text-blue-800 hover:underline">
							{t('korekty.list.table.assignPlacowka')}
						</button>
					)}
				</p>
			</div>

			{/* Right side: actions */}
			<div className="flex flex-wrap gap-2">
				{!hasInfaktInvoice && (
					<button
						type="button"
						onClick={onPushToInfakt}
						disabled={pushing}
						className={`inline-flex items-center gap-2 rounded-lg px-4 py-2.5 text-sm font-medium shadow-sm ${
							pushing
								? 'bg-gray-400 text-white cursor-not-allowed'
								: 'bg-emerald-600 text-white hover:bg-emerald-700'
						}`}>
						<img src="/infakt-logo.svg" alt="" className="h-4 w-4 shrink-0 brightness-0 invert" />
						{pushing
							? t('korekty.details.header.pushingToInfakt')
							: t('korekty.details.header.pushToInfakt')}
					</button>
				)}

				{!hasInfaktInvoice && (
					<button
						type="button"
						onClick={onEdit}
						className="rounded-lg bg-indigo-600 px-4 py-2.5 text-sm font-medium text-white hover:bg-indigo-700">
						{t('korekty.details.header.edit')}
					</button>
				)}

				{hasInfaktInvoice && (
					<button
						type="button"
						onClick={handleDownload}
						className="rounded-lg bg-white px-4 py-2.5 text-sm font-medium text-gray-900 border border-gray-300 hover:bg-gray-50">
						{t('korekty.details.header.download')}
					</button>
				)}

				{!hasInfaktInvoice && (
					<button
						type="button"
						onClick={onDelete}
						disabled={deleting}
						className={`rounded-lg px-4 py-2.5 text-sm font-medium shadow-sm ${
							deleting ? 'bg-gray-400 text-white cursor-not-allowed' : 'bg-red-600 text-white hover:bg-red-700'
						}`}>
						{deleting ? t('korekty.details.header.deleting') : t('korekty.details.header.delete')}
					</button>
				)}
			</div>
		</header>
	)
}
